import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Avatar,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  IconButton,
  Tooltip,
  Autocomplete,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  Add as AddIcon,
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  Event as EventIcon,
  Search as SearchIcon,
  Star as StarIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import openScheduleService, { type OpenSchedule } from '../services/openScheduleService';
import visitorService from '../services/visitorService';
import { getPropertyAttendees, updatePropertyAttendee, deletePropertyAttendee, searchAttendees, requestContract, sendContract, resendContract, sendApplicationForm, type PropertyAttendee, type SearchAttendeeResult } from '../services/propertyService';
import type { Property } from '../types/property';
import { useAuth } from '../hooks/useAuth';
import attendeeTagService from '../services/attendeeTagService';

interface PropertyAttendeesProps {
  property: Property;
  agencySlug: string;
}

interface Attendee {
  name: string;
  email?: string;
  phone?: string;
  notes?: string;
  registeredAt: string;
  sessionDate?: string;
  sessionTime?: string;
  sessions?: number;
  visitDate?: string;
  visitType?: string;
  feedback?: string;
  interestLevel?: 'high' | 'medium' | 'low' | 'none';
  priceFeedback?: string;
  followUpIntent?: 'wants_contract' | 'wants_second_inspection' | 'considering' | 'not_interested' | 'no_follow_up';
  objections?: string;
  keenBuyer?: boolean;
  attendeeRequestsContract?: boolean;
  contractRequested?: boolean;
  contractSent?: boolean;
  contractSentAt?: string;
  attendeeRequestsApplicationForm?: boolean;
  applicationFormSent?: boolean;
  applicationFormSentAt?: string;
  source: 'open_schedule' | 'property';
  _id?: string;
  recordedByUser?: {
    name: string;
    email: string;
  };
  tags?: string[];
}

const PropertyAttendees: React.FC<PropertyAttendeesProps> = ({ property, agencySlug }) => {
  const { agency } = useAuth();
  const planName = (agency?.subscription?.planName || agency?.subscriptionPlan || 'Free').toLowerCase();
  const hasAttendeeAccess = ['professional', 'agency premium', 'starter', 'premium', 'enterprise', 'demo', 'individual', 'elite individual'].includes(planName);
  const [attendees, setAttendees] = useState<Attendee[]>([]);
  const [loading, setLoading] = useState(true);
  const [schedule, setSchedule] = useState<OpenSchedule | null>(null);
  const [propertyAttendees, setPropertyAttendees] = useState<PropertyAttendee[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'open_schedule' | 'property'>('property');
  const [isEditing, setIsEditing] = useState(false);
  const [editingAttendeeId, setEditingAttendeeId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [attendeeToDelete, setAttendeeToDelete] = useState<{ id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [formData, setFormData] = useState({
    sessionId: '',
    name: '',
    email: '',
    phone: '',
    notes: '',
    visitDate: '',
    visitType: 'open_house' as 'viewing' | 'inspection' | 'open_house' | 'private_viewing' | 'other',
    feedback: '',
    interestLevel: '' as '' | 'high' | 'medium' | 'low' | 'none',
    priceFeedback: '',
    followUpIntent: '' as '' | 'wants_contract' | 'wants_second_inspection' | 'considering' | 'not_interested' | 'no_follow_up',
    objections: '',
    attendeeRequestsContract: false,
    attendeeRequestsApplicationForm: false,
    tags: [] as string[]
  });
  const [searchResults, setSearchResults] = useState<SearchAttendeeResult[]>([]);
  const [tagOptions, setTagOptions] = useState<string[]>([]);
  const [tagOptionsLoading, setTagOptionsLoading] = useState(false);
  const [resendingContractId, setResendingContractId] = useState<string | null>(null);
  const [sendingApplicationFormId, setSendingApplicationFormId] = useState<string | null>(null);
  const normalizeTagValue = (value: string) => value.trim();

  const handleTagSelectionChange = async (values: string[]) => {
    const cleaned = Array.from(new Set(values.map(normalizeTagValue).filter(Boolean)));
    setFormData(prev => ({ ...prev, tags: cleaned }));

    const missing = cleaned.filter(
      tag => !tagOptions.some(existing => existing.toLowerCase() === tag.toLowerCase())
    );

    if (!agencySlug || missing.length === 0) {
      return;
    }

    for (const tag of missing) {
      try {
        await attendeeTagService.createAttendeeTag(agencySlug, tag);
        setTagOptions(prev =>
          prev.some(existing => existing.toLowerCase() === tag.toLowerCase()) ? prev : [...prev, tag]
        );
      } catch (err) {
        console.error('Failed to create attendee tag', err);
      }
    }
  };

  if (!hasAttendeeAccess) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        Attendee tracking is available on Starter plans and above. Upgrade to record open-house visitors, manage follow ups, and send contracts directly from Workflow Estate.
      </Alert>
    );
  }
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    const fetchAttendees = async () => {
      try {
        setLoading(true);
        
        const [scheduleData, propertyAttendeesData] = await Promise.all([
          openScheduleService.getOpenSchedule(property._id, agencySlug, true).catch(() => null),
          getPropertyAttendees(agencySlug, property._id).catch(() => [])
        ]);

        if (scheduleData && scheduleData._id) {
          console.log('PropertyAttendees: Setting schedule with sessions:', scheduleData.sessions?.length || 0);
          setSchedule(scheduleData);
          if (!scheduleData.sessions || scheduleData.sessions.length === 0) {
            console.warn('PropertyAttendees: Schedule has no sessions!', scheduleData);
          }
        } else {
          setSchedule(null);
        }

        setPropertyAttendees(propertyAttendeesData);
        const propertyAttendeesList: Attendee[] = propertyAttendeesData.map((pa: PropertyAttendee) => ({
          name: pa.name,
          email: pa.email,
          phone: pa.phone,
          notes: pa.notes,
          registeredAt: pa.recordedAt,
          visitDate: pa.visitDate,
          visitType: pa.visitType,
          feedback: pa.feedback,
          interestLevel: pa.interestLevel,
          priceFeedback: pa.priceFeedback,
          followUpIntent: pa.followUpIntent,
          objections: pa.objections,
          keenBuyer: pa.keenBuyer,
          attendeeRequestsContract: pa.attendeeRequestsContract,
          contractRequested: pa.contractRequested,
          contractSent: pa.contractSent,
          contractSentAt: pa.contractSentAt,
          attendeeRequestsApplicationForm: pa.attendeeRequestsApplicationForm,
          applicationFormSent: pa.applicationFormSent,
          applicationFormSentAt: pa.applicationFormSentAt,
          source: 'property',
          _id: pa._id,
          recordedByUser: pa.recordedByUser,
          tags: pa.tags || []
        }));

        setAttendees(propertyAttendeesList);
      } catch (err: any) {
        console.error('Failed to fetch attendees:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAttendees();
  }, [property._id, agencySlug]);

  useEffect(() => {
    if (!agencySlug) return;
    let isMounted = true;

    const loadTagOptions = async () => {
      try {
        setTagOptionsLoading(true);
        const data = await attendeeTagService.getAttendeeTags(agencySlug);
        if (!isMounted) return;
        setTagOptions(Array.from(new Set(data.allTags)));
      } catch (err) {
        console.error('Failed to load attendee tags', err);
      } finally {
        if (isMounted) {
          setTagOptionsLoading(false);
        }
      }
    };

    loadTagOptions();

    return () => {
      isMounted = false;
    };
  }, [agencySlug]);

  // Calculate available sessions (past sessions only, sorted by date descending)
  const availableSessions = React.useMemo(() => {
    if (!schedule?.sessions || !Array.isArray(schedule.sessions)) {
      console.log('PropertyAttendees: No schedule or sessions array');
      return [];
    }
    
    const now = new Date();
    console.log('PropertyAttendees: Filtering sessions. Total sessions:', schedule.sessions.length);
    
    const filtered = schedule.sessions.filter((session: any) => {
      // Skip cancelled sessions
      if (session.status === 'cancelled') {
        return false;
      }
      
      // Always include completed sessions
      if (session.status === 'completed') {
        return true;
      }
      
      // For in_progress sessions, include them
      if (session.status === 'in_progress') {
        return true;
      }
      
      // For scheduled sessions, check if the session date/time has passed
      if (session.status === 'scheduled' && session.date) {
        try {
          // Parse session.date (could be string or Date object)
          const sessionDate = new Date(session.date);
          
          // Check if date is valid
          if (isNaN(sessionDate.getTime())) {
            return false;
          }
          
          // Parse startTime (format: "HH:MM") and set hours/minutes
          if (session.startTime) {
            const [hours, minutes] = session.startTime.split(':').map(Number);
            if (!isNaN(hours) && !isNaN(minutes)) {
              sessionDate.setHours(hours, minutes, 0, 0);
            }
          } else {
            // If no startTime, set to end of day to include the whole day
            sessionDate.setHours(23, 59, 59, 999);
          }
          
          // Only include if session date/time is in the past
          return sessionDate < now;
        } catch (error) {
          // If date parsing fails, exclude it
          console.warn('Error parsing session date:', session.date, error);
          return false;
        }
      }
      
      return false;
    });
    
    console.log('PropertyAttendees: Filtered sessions count:', filtered.length);
    if (filtered.length === 0 && schedule.sessions.length > 0) {
      console.log('PropertyAttendees: No past sessions passed filter, falling back to all sessions');
      return [...schedule.sessions].sort((a: any, b: any) => {
        try {
          const dateA = new Date(a.date).getTime();
          const dateB = new Date(b.date).getTime();
          if (dateA === dateB && a.startTime && b.startTime) {
            return b.startTime.localeCompare(a.startTime);
          }
          return dateB - dateA;
        } catch {
          return 0;
        }
      });
    }
    
    return filtered.sort((a: any, b: any) => {
      // Sort by date descending (most recent first)
      try {
        const dateA = new Date(a.date).getTime();
        const dateB = new Date(b.date).getTime();
        
        // If dates are equal, sort by startTime
        if (dateA === dateB && a.startTime && b.startTime) {
          return b.startTime.localeCompare(a.startTime);
        }
        
        return dateB - dateA;
      } catch {
        return 0;
      }
    });
  }, [schedule]);

  // Fetch schedule when dialog opens (always fetch to ensure we have latest data)
  useEffect(() => {
    if (dialogOpen) {
      console.log('PropertyAttendees: Dialog opened, fetching schedule...');
      console.log('PropertyAttendees: property._id =', property._id, 'agencySlug =', agencySlug);
      const fetchSchedule = async () => {
        try {
          console.log('PropertyAttendees: About to call getOpenSchedule with includePastSessions=true');
          const scheduleData = await openScheduleService.getOpenSchedule(property._id, agencySlug, true);
          console.log('PropertyAttendees: Fetched schedule:', scheduleData?._id, 'sessions:', scheduleData?.sessions?.length);
          if (scheduleData && scheduleData._id) {
            setSchedule(scheduleData);
          } else {
            console.warn('PropertyAttendees: Schedule data is null or has no _id:', scheduleData);
            setSchedule(null);
          }
        } catch (err) {
          console.error('PropertyAttendees: Failed to fetch schedule:', err);
          setSchedule(null);
        }
      };
      fetchSchedule();
    }
  }, [dialogOpen, property._id, agencySlug]);

  useEffect(() => {
    // Reset form when dialog opens or mode changes
    if (dialogOpen) {
      // Set default to the most recent past session (for both modes)
      let defaultSessionId = '';
      if (availableSessions.length > 0) {
        // availableSessions is already sorted by date descending (most recent first)
        defaultSessionId = availableSessions[0]._id || '';
      }
      
      setFormData({ 
        sessionId: defaultSessionId, 
        name: '', 
        email: '', 
        phone: '', 
        notes: '', 
        visitDate: '', 
        visitType: 'open_house',
        feedback: '',
        interestLevel: '',
        priceFeedback: '',
        followUpIntent: '',
        objections: '',
        attendeeRequestsContract: false,
        attendeeRequestsApplicationForm: false,
        tags: []
      });
      setError(null);
    }
  }, [dialogOpen, dialogMode, availableSessions]);

  const handleRegisterAttendee = async () => {
    if (!formData.name || !formData.phone) {
      setError('Name and phone are required');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      
      const [firstName, ...lastNameParts] = formData.name.split(' ');
      const lastName = lastNameParts.join(' ');

      // Editing existing property attendee
      if (isEditing && editingAttendeeId) {
        await updatePropertyAttendee(agencySlug, property._id, editingAttendeeId, {
          name: formData.name,
          email: formData.email || undefined,
          phone: formData.phone,
          notes: formData.notes || undefined,
          visitDate: formData.visitDate || undefined,
          visitType: formData.visitType,
          feedback: formData.feedback || undefined,
          interestLevel: formData.interestLevel || undefined,
          priceFeedback: formData.priceFeedback || undefined,
          followUpIntent: formData.followUpIntent || undefined,
          objections: formData.objections || undefined,
          attendeeRequestsContract: formData.attendeeRequestsContract,
          attendeeRequestsApplicationForm: formData.attendeeRequestsApplicationForm,
          tags: formData.tags
        });
      } else {
        // Unified registration via visitorService
        // This handles: Attendee upsert, Activity log, Property sync, and Session auto-sync
        await visitorService.createVisitor(agencySlug, {
          propertyId: property._id,
          sessionId: formData.sessionId, // Pass sessionId for precise session sync
          firstName: firstName,
          lastName: lastName || '.', // Ensure last name isn't empty
          email: formData.email,
          phone: formData.phone,
          notes: formData.notes,
          feedback: formData.feedback,
          interestLevel: formData.interestLevel || undefined,
          priceFeedback: formData.priceFeedback,
          followUpIntent: formData.followUpIntent || undefined,
          objections: formData.objections,
          tags: formData.tags,
          visitType: formData.visitType,
          contractSent: formData.attendeeRequestsContract,
          requestsApplicationForm: formData.attendeeRequestsApplicationForm,
          inspectionDate: new Date().toISOString().split('T')[0],
          inspectionTime: new Date().toTimeString().slice(0, 5),
          source: 'web_dashboard'
        });
      }

      // Refresh attendees list
      const [scheduleData, propertyAttendeesData] = await Promise.all([
        openScheduleService.getOpenSchedule(property._id, agencySlug, true).catch(() => null),
        getPropertyAttendees(agencySlug, property._id).catch(() => [])
      ]);

      if (scheduleData && scheduleData._id && scheduleData.sessions) {
        setSchedule(scheduleData);
      }

      setPropertyAttendees(propertyAttendeesData);
      const propertyAttendeesList: Attendee[] = propertyAttendeesData.map((pa: PropertyAttendee) => ({
        name: pa.name,
        email: pa.email,
        phone: pa.phone,
        notes: pa.notes,
        registeredAt: pa.recordedAt,
        visitDate: pa.visitDate,
        visitType: pa.visitType,
        feedback: pa.feedback,
        interestLevel: pa.interestLevel,
        priceFeedback: pa.priceFeedback,
        followUpIntent: pa.followUpIntent,
        objections: pa.objections,
        attendeeRequestsContract: pa.attendeeRequestsContract,
        keenBuyer: pa.keenBuyer,
        contractRequested: pa.contractRequested,
        contractSent: pa.contractSent,
        contractSentAt: pa.contractSentAt,
        attendeeRequestsApplicationForm: pa.attendeeRequestsApplicationForm,
        applicationFormSent: pa.applicationFormSent,
        applicationFormSentAt: pa.applicationFormSentAt,
        source: 'property',
        _id: pa._id,
        recordedByUser: pa.recordedByUser,
        tags: pa.tags || []
      }));

      setAttendees(propertyAttendeesList);

      setDialogOpen(false);
      setIsEditing(false);
      setEditingAttendeeId(null);
      setFormData({ sessionId: '', name: '', email: '', phone: '', notes: '', visitDate: '', visitType: 'open_house', feedback: '', interestLevel: '', priceFeedback: '', followUpIntent: '', objections: '', attendeeRequestsContract: false, attendeeRequestsApplicationForm: false, tags: [] });
    } catch (err: any) {
      console.error('Error registering attendee:', err);
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to register attendee');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAttendee = (attendeeId: string, attendeeName: string) => {
    setAttendeeToDelete({ id: attendeeId, name: attendeeName });
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!attendeeToDelete) return;

    try {
      setDeleting(true);
      await deletePropertyAttendee(agencySlug, property._id, attendeeToDelete.id);
      
      // Refresh attendees list
      const propertyAttendeesData = await getPropertyAttendees(agencySlug, property._id);
      setPropertyAttendees(propertyAttendeesData);
      
      const propertyAttendeesList: Attendee[] = propertyAttendeesData.map((pa: PropertyAttendee) => ({
        name: pa.name,
        email: pa.email,
        phone: pa.phone,
        notes: pa.notes,
        registeredAt: pa.recordedAt,
        visitDate: pa.visitDate,
        visitType: pa.visitType,
        feedback: pa.feedback,
        interestLevel: pa.interestLevel,
        priceFeedback: pa.priceFeedback,
        followUpIntent: pa.followUpIntent,
        objections: pa.objections,
        attendeeRequestsContract: pa.attendeeRequestsContract,
        source: 'property',
        _id: pa._id,
        recordedByUser: pa.recordedByUser,
        tags: pa.tags || []
      }));

      setAttendees(propertyAttendeesList);
      setDeleteDialogOpen(false);
      setAttendeeToDelete(null);
    } catch (err: any) {
      console.error('Error deleting attendee:', err);
      setError(err.response?.data?.error || 'Failed to delete attendee');
      setDeleting(false);
    }
  };

  const openEditDialog = (attendee: Attendee) => {
    setDialogMode('property');
    setIsEditing(true);
    setEditingAttendeeId(attendee._id || null);
    setFormData({
      sessionId: '',
      name: attendee.name || '',
      email: attendee.email || '',
      phone: attendee.phone || '',
      notes: attendee.notes || '',
      visitDate: attendee.visitDate || '',
      visitType: (attendee.visitType as any) || 'open_house',
      feedback: attendee.feedback || '',
      interestLevel: attendee.interestLevel || '',
      priceFeedback: attendee.priceFeedback || '',
      followUpIntent: attendee.followUpIntent || '',
      objections: attendee.objections || '',
      attendeeRequestsContract: !!attendee.attendeeRequestsContract,
      attendeeRequestsApplicationForm: !!attendee.attendeeRequestsApplicationForm,
      tags: attendee.tags || []
    });
    setDialogOpen(true);
  };

  const handleRequestContract = async (attendeeId: string) => {
    try {
      await requestContract(agencySlug, property._id, attendeeId);
      
      // Refresh attendees list
      const propertyAttendeesData = await getPropertyAttendees(agencySlug, property._id);
      setPropertyAttendees(propertyAttendeesData);
      
      const propertyAttendeesList: Attendee[] = propertyAttendeesData.map((pa: PropertyAttendee) => ({
        name: pa.name,
        email: pa.email,
        phone: pa.phone,
        notes: pa.notes,
        registeredAt: pa.recordedAt,
        visitDate: pa.visitDate,
        visitType: pa.visitType,
        feedback: pa.feedback,
        interestLevel: pa.interestLevel,
        priceFeedback: pa.priceFeedback,
        followUpIntent: pa.followUpIntent,
        objections: pa.objections,
        keenBuyer: pa.keenBuyer,
        attendeeRequestsContract: pa.attendeeRequestsContract,
        contractRequested: pa.contractRequested,
        contractSent: pa.contractSent,
        contractSentAt: pa.contractSentAt,
        attendeeRequestsApplicationForm: pa.attendeeRequestsApplicationForm,
        applicationFormSent: pa.applicationFormSent,
        applicationFormSentAt: pa.applicationFormSentAt,
        source: 'property',
        _id: pa._id,
        recordedByUser: pa.recordedByUser,
        tags: pa.tags || []
      }));

      setAttendees(propertyAttendeesList);
    } catch (err: any) {
      console.error('Error requesting contract:', err);
      alert(err.response?.data?.error || 'Failed to request contract');
    }
  };

  const handleSendContract = async (attendeeId: string) => {
    try {
      await sendContract(agencySlug, property._id, attendeeId);
      
      // Refresh attendees list
      const propertyAttendeesData = await getPropertyAttendees(agencySlug, property._id);
      setPropertyAttendees(propertyAttendeesData);
      
      const propertyAttendeesList: Attendee[] = propertyAttendeesData.map((pa: PropertyAttendee) => ({
        name: pa.name,
        email: pa.email,
        phone: pa.phone,
        notes: pa.notes,
        registeredAt: pa.recordedAt,
        visitDate: pa.visitDate,
        visitType: pa.visitType,
        feedback: pa.feedback,
        interestLevel: pa.interestLevel,
        priceFeedback: pa.priceFeedback,
        followUpIntent: pa.followUpIntent,
        objections: pa.objections,
        attendeeRequestsContract: pa.attendeeRequestsContract,
        keenBuyer: pa.keenBuyer,
        contractRequested: pa.contractRequested,
        contractSent: pa.contractSent,
        contractSentAt: pa.contractSentAt,
        attendeeRequestsApplicationForm: pa.attendeeRequestsApplicationForm,
        applicationFormSent: pa.applicationFormSent,
        applicationFormSentAt: pa.applicationFormSentAt,
        source: 'property',
        _id: pa._id,
        recordedByUser: pa.recordedByUser,
        tags: pa.tags || []
      }));

      setAttendees(propertyAttendeesList);
    } catch (err: any) {
      console.error('Error sending contract:', err);
      alert(err.response?.data?.error || 'Failed to send contract');
    }
  };

  const handleSendApplicationForm = async (attendeeId: string) => {
    try {
      setSendingApplicationFormId(attendeeId);
      await sendApplicationForm(agencySlug, property._id, attendeeId);
      const propertyAttendeesData = await getPropertyAttendees(agencySlug, property._id);
      setPropertyAttendees(propertyAttendeesData);
      const propertyAttendeesList: Attendee[] = propertyAttendeesData.map((pa: PropertyAttendee) => ({
        name: pa.name,
        email: pa.email,
        phone: pa.phone,
        notes: pa.notes,
        registeredAt: pa.recordedAt,
        visitDate: pa.visitDate,
        visitType: pa.visitType,
        feedback: pa.feedback,
        interestLevel: pa.interestLevel,
        priceFeedback: pa.priceFeedback,
        followUpIntent: pa.followUpIntent,
        objections: pa.objections,
        attendeeRequestsContract: pa.attendeeRequestsContract,
        keenBuyer: pa.keenBuyer,
        contractRequested: pa.contractRequested,
        contractSent: pa.contractSent,
        contractSentAt: pa.contractSentAt,
        attendeeRequestsApplicationForm: pa.attendeeRequestsApplicationForm,
        applicationFormSent: pa.applicationFormSent,
        applicationFormSentAt: pa.applicationFormSentAt,
        source: 'property',
        _id: pa._id,
        recordedByUser: pa.recordedByUser,
        tags: pa.tags || []
      }));
      setAttendees(propertyAttendeesList);
    } catch (err: any) {
      console.error('Error sending application form:', err);
      alert(err.response?.data?.error || 'Failed to send application form');
    } finally {
      setSendingApplicationFormId(null);
    }
  };

  const handleResendContract = async (attendeeId: string) => {
    if (resendingContractId) {
      return; // Prevent multiple simultaneous requests
    }
    
    try {
      setResendingContractId(attendeeId);
      await resendContract(agencySlug, property._id, attendeeId);
      
      // Refresh attendees list
      const propertyAttendeesData = await getPropertyAttendees(agencySlug, property._id);
      setPropertyAttendees(propertyAttendeesData);
      
      const propertyAttendeesList: Attendee[] = propertyAttendeesData.map((pa: PropertyAttendee) => ({
        name: pa.name,
        email: pa.email,
        phone: pa.phone,
        notes: pa.notes,
        registeredAt: pa.recordedAt,
        visitDate: pa.visitDate,
        visitType: pa.visitType,
        feedback: pa.feedback,
        interestLevel: pa.interestLevel,
        priceFeedback: pa.priceFeedback,
        followUpIntent: pa.followUpIntent,
        objections: pa.objections,
        attendeeRequestsContract: pa.attendeeRequestsContract,
        keenBuyer: pa.keenBuyer,
        contractRequested: pa.contractRequested,
        contractSent: pa.contractSent,
        contractSentAt: pa.contractSentAt,
        source: 'property',
        _id: pa._id,
        recordedByUser: pa.recordedByUser,
        tags: pa.tags || []
      }));

      setAttendees(propertyAttendeesList);
    } catch (err: any) {
      console.error('Error resending contract:', err);
      alert(err.response?.data?.error || 'Failed to resend contract');
    } finally {
      setResendingContractId(null);
    }
  };

  const formatSessionLabel = (session: any) => {
    try {
      const date = new Date(session.date);
      const dateStr = date.toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
      return `${dateStr} at ${session.startTime}`;
    } catch {
      return `${session.date} at ${session.startTime}`;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={2}>
        <CircularProgress size={24} sx={{ mr: 2 }} />
        <Typography>Loading attendees...</Typography>
      </Box>
    );
  }

  const hasSchedule = schedule && schedule._id;

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const getInitials = (name: string) => {
    const words = name.trim().split(' ');
    if (words.length >= 2) {
      return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Filter attendees based on search query
  const filteredAttendees = attendees.filter((attendee) => {
    if (!searchQuery.trim()) {
      return true;
    }
    
    const query = searchQuery.toLowerCase().trim();
    const nameMatch = attendee.name?.toLowerCase().includes(query);
    const phoneMatch = attendee.phone?.toLowerCase().includes(query);
    const emailMatch = attendee.email?.toLowerCase().includes(query);
    const tagMatch = attendee.tags?.some(tag => tag.toLowerCase().includes(query));
    
    return nameMatch || phoneMatch || emailMatch || tagMatch;
  });

  console.log('PropertyAttendees render - dialogOpen:', dialogOpen, 'availableSessions:', availableSessions.length, 'schedule:', schedule?._id);

  return (
    <Box>
      <Box mb={2} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Typography variant="h6">Attendees ({filteredAttendees.length}{searchQuery ? ` of ${attendees.length}` : ''})</Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => {
              // Default to 'property' mode - it can handle both with/without session
              setDialogMode('property');
              setDialogOpen(true);
            }}
          >
            Record Attendee
          </Button>
        </Box>
      </Box>

      {/* Search Input */}
      {attendees.length > 0 && (
        <Box mb={2}>
          <TextField
            fullWidth
            placeholder="Search by name, phone, or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
            sx={{ mb: 2 }}
          />
        </Box>
      )}

      {attendees.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No attendees recorded yet. Click "Record Attendee" to add one.
        </Typography>
      )}

      {filteredAttendees.length === 0 && attendees.length > 0 && (
        <Typography variant="body2" color="text.secondary">
          No attendees match your search query.
        </Typography>
      )}

      {filteredAttendees.length > 0 && (
        <>
          <Grid container spacing={2}>
            {filteredAttendees.map((attendee, index) => {
              const initials = getInitials(attendee.name);
              const uniqueKey = attendee._id || `${attendee.source}-${attendee.phone}-${attendee.name}-${index}`;

              return (
                <Grid item xs={12} sm={6} md={4} key={uniqueKey}>
                  <Card variant="outlined" sx={{ height: '100%', position: 'relative' }}>
                    <CardContent>
                      <Box display="flex" alignItems="flex-start" gap={2} mb={2}>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                          {initials}
                        </Avatar>
                        <Box flex={1}>
                          <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Typography variant="subtitle1" fontWeight="medium">
                              {attendee.name}
                            </Typography>
                            {attendee.source === 'property' && attendee._id && (
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Tooltip title="Edit attendee">
                                  <IconButton
                                    size="small"
                                    onClick={() => openEditDialog(attendee)}
                                    sx={{ ml: 1 }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete attendee">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteAttendee(attendee._id!, attendee.name)}
                                    sx={{ ml: 1 }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            )}
                          </Box>
                          <Box display="flex" gap={1} mt={0.5} flexWrap="wrap">
                            {attendee.keenBuyer && (
                              <Chip 
                                label="Keen Buyer" 
                                size="small" 
                                color="success" 
                                variant="filled"
                                icon={<StarIcon />}
                              />
                            )}
                            {attendee.sessions && attendee.sessions > 1 && (
                              <Chip 
                                label={`${attendee.sessions} visits`} 
                                size="small" 
                                color="primary" 
                                variant="outlined"
                              />
                            )}
                            {attendee.source === 'open_schedule' && (
                              <Chip 
                                label="Open House" 
                                size="small" 
                                color="info" 
                                variant="outlined"
                              />
                            )}
                            {attendee.visitType && (
                              <Chip 
                                label={attendee.visitType.replace('_', ' ')} 
                                size="small" 
                                color="secondary" 
                                variant="outlined"
                              />
                            )}
                            {attendee.tags && attendee.tags.length > 0 && attendee.tags.map((tag) => (
                              <Chip
                                key={`${uniqueKey}-${tag}`}
                                label={tag}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        </Box>
                      </Box>
                      
                      <Divider sx={{ my: 1 }} />
                      <Box display="flex" flexDirection="column" gap={1}>
                        {attendee.email && (
                          <Box display="flex" alignItems="center" gap={1}>
                            <EmailIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {attendee.email}
                            </Typography>
                          </Box>
                        )}
                        {attendee.phone && (
                          <Box display="flex" alignItems="center" gap={1}>
                            <PhoneIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {attendee.phone}
                            </Typography>
                          </Box>
                        )}
                        <Box display="flex" alignItems="center" gap={1}>
                          <CalendarIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {attendee.sessionDate 
                              ? `First visit: ${formatDate(attendee.sessionDate)}${attendee.sessionTime ? ` at ${attendee.sessionTime}` : ''}`
                              : attendee.visitDate 
                                ? `Visit: ${formatDate(attendee.visitDate)}`
                                : `Recorded: ${formatDate(attendee.registeredAt)}`
                            }
                          </Typography>
                        </Box>
                        {attendee.recordedByUser && (
                          <Typography variant="caption" color="text.secondary">
                            Recorded by: {attendee.recordedByUser.name}
                          </Typography>
                        )}
                        {(attendee.feedback || attendee.interestLevel || attendee.priceFeedback || attendee.followUpIntent || attendee.objections) && (
                          <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                            {attendee.interestLevel && (
                              <Chip size="small" label={`Interest: ${attendee.interestLevel}`} color={attendee.interestLevel === 'high' ? 'success' : 'default'} sx={{ mb: 1 }} />
                            )}
                            {attendee.feedback && (
                              <Typography variant="body2">
                                {attendee.feedback}
                              </Typography>
                            )}
                            {attendee.priceFeedback && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Price feedback: {attendee.priceFeedback}
                              </Typography>
                            )}
                            {attendee.objections && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Objections: {attendee.objections}
                              </Typography>
                            )}
                          </Box>
                        )}
                        {attendee.source === 'property' && attendee._id && (attendee.contractRequested || attendee.attendeeRequestsContract) && property.saleType !== 'rental' && (
                          <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                            {!attendee.contractSent && (
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                onClick={() => handleSendContract(attendee._id!)}
                                disabled={!(property.contract?.key || property.contract?.url) || !attendee.email}
                              >
                                Send Contract
                              </Button>
                            )}
                            {attendee.contractSent && (
                              <>
                                <Chip 
                                  label={`Contract sent ${attendee.contractSentAt ? formatDate(attendee.contractSentAt) : ''}`}
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                />
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleResendContract(attendee._id!)}
                                  disabled={!(property.contract?.key || property.contract?.url) || !attendee.email || resendingContractId === attendee._id}
                                >
                                  {resendingContractId === attendee._id ? 'Resending...' : 'Resend'}
                                </Button>
                              </>
                            )}
                          </Box>
                        )}
                        {property.saleType === 'rental' && attendee.source === 'property' && attendee._id && (
                          <Box display="flex" gap={1} mt={1} flexWrap="wrap" alignItems="center">
                            {attendee.attendeeRequestsApplicationForm && (
                              <Chip label="Wants application form" size="small" variant="outlined" color="primary" />
                            )}
                            {!attendee.applicationFormSent && (
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                onClick={() => handleSendApplicationForm(attendee._id!)}
                                disabled={!attendee.email || sendingApplicationFormId === attendee._id}
                              >
                                {sendingApplicationFormId === attendee._id ? 'Sending...' : 'Send application form'}
                              </Button>
                            )}
                            {attendee.applicationFormSent && (
                              <Chip
                                label={`Application form sent ${attendee.applicationFormSentAt ? formatDate(attendee.applicationFormSentAt) : ''}`}
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}

      {/* Record Attendee Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => {
          setDialogOpen(false);
          setIsEditing(false);
          setEditingAttendeeId(null);
          setFormData({ sessionId: '', name: '', email: '', phone: '', notes: '', visitDate: '', visitType: 'open_house', feedback: '', interestLevel: '', priceFeedback: '', followUpIntent: '', objections: '', attendeeRequestsContract: false, attendeeRequestsApplicationForm: false, tags: [] });
          setError(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {isEditing ? 'Edit Attendee' : 'Record Attendee'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* Session dropdown - only shown when recording new attendee, not when editing */}
            {!isEditing && (
              <FormControl fullWidth>
                <InputLabel>Session (optional)</InputLabel>
                <Select
                  value={formData.sessionId || ''}
                  onChange={(e) => setFormData({ ...formData, sessionId: e.target.value })}
                  onOpen={() => {
                    // Fetch schedule when dropdown opens if not already loaded
                    if (!schedule) {
                      console.log('PropertyAttendees: Session dropdown opened, fetching schedule...');
                      const fetchSchedule = async () => {
                        try {
                          const scheduleData = await openScheduleService.getOpenSchedule(property._id, agencySlug, true);
                          if (scheduleData && scheduleData._id) {
                            setSchedule(scheduleData);
                          }
                        } catch (err) {
                          console.error('PropertyAttendees: Failed to fetch schedule on dropdown open:', err);
                        }
                      };
                      fetchSchedule();
                    }
                  }}
                  label="Session (optional)"
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>Select a session (optional)</em>
                  </MenuItem>
                  {availableSessions.length === 0 && schedule && (
                    <MenuItem value="" disabled>
                      <em>No past sessions available</em>
                    </MenuItem>
                  )}
                  {availableSessions.map((session: any) => (
                    <MenuItem key={session._id} value={session._id}>
                      {formatSessionLabel(session)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Name with autocomplete */}
            <Autocomplete
              freeSolo
              options={searchResults.map((a) => a.name).filter((name, index, self) => self.indexOf(name) === index)}
              loading={searchLoading}
              value={formData.name}
              onInputChange={async (event, newValue) => {
                setFormData({ ...formData, name: newValue || '' });
                if (newValue && newValue.trim().length >= 2) {
                  setSearchLoading(true);
                  try {
                    const results = await searchAttendees(agencySlug, newValue);
                    setSearchResults(results);
                  } catch (err) {
                    console.error('Error searching attendees:', err);
                    setSearchResults([]);
                  } finally {
                    setSearchLoading(false);
                  }
                } else {
                  setSearchResults([]);
                }
              }}
              onChange={(event, value) => {
                if (value && typeof value === 'string') {
                  // Check if this value exists in searchResults (user selected from dropdown)
                  const selectedAttendee = searchResults.find(a => a.name === value);
                  if (selectedAttendee) {
                    // User selected from dropdown - auto-fill all fields
                    setFormData({
                      ...formData,
                      name: selectedAttendee.name,
                      phone: selectedAttendee.phone || '',
                      email: selectedAttendee.email || '',
                      tags: selectedAttendee.tags || formData.tags
                    });
                  } else {
                    // User typed a new value that's not in the list
                    setFormData({ ...formData, name: value });
                  }
                } else {
                  // Value cleared
                  setFormData({ ...formData, name: '' });
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Name"
                  required
                  fullWidth
                />
              )}
            />

            {/* Phone with autocomplete */}
            <Autocomplete
              freeSolo
              options={searchResults.map((a) => a.phone).filter((phone): phone is string => !!phone && phone.trim() !== '').filter((phone, index, self) => self.indexOf(phone) === index)}
              loading={searchLoading}
              value={formData.phone}
              onInputChange={async (event, newValue) => {
                setFormData({ ...formData, phone: newValue || '' });
                if (newValue && newValue.trim().length >= 2) {
                  setSearchLoading(true);
                  try {
                    const results = await searchAttendees(agencySlug, newValue);
                    setSearchResults(results);
                  } catch (err) {
                    console.error('Error searching attendees:', err);
                    setSearchResults([]);
                  } finally {
                    setSearchLoading(false);
                  }
                } else {
                  setSearchResults([]);
                }
              }}
              onChange={(event, value) => {
                if (value && typeof value === 'string') {
                  // Check if this value exists in searchResults (user selected from dropdown)
                  const selectedAttendee = searchResults.find(a => a.phone === value);
                  if (selectedAttendee) {
                    // User selected from dropdown - auto-fill all fields
                    setFormData({
                      ...formData,
                      name: selectedAttendee.name,
                      phone: selectedAttendee.phone || '',
                      email: selectedAttendee.email || '',
                      tags: selectedAttendee.tags || formData.tags
                    });
                  } else {
                    // User typed a new value that's not in the list
                    setFormData({ ...formData, phone: value });
                  }
                } else {
                  // Value cleared
                  setFormData({ ...formData, phone: '' });
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Phone"
                  required
                  fullWidth
                />
              )}
            />

            {/* Email with autocomplete */}
            <Autocomplete
              freeSolo
              options={searchResults.map((a) => a.email).filter((email): email is string => !!email && email.trim() !== '').filter((email, index, self) => self.indexOf(email) === index)}
              loading={searchLoading}
              value={formData.email}
              onInputChange={async (event, newValue) => {
                setFormData({ ...formData, email: newValue || '' });
                if (newValue && newValue.trim().length >= 2) {
                  setSearchLoading(true);
                  try {
                    const results = await searchAttendees(agencySlug, newValue);
                    setSearchResults(results);
                  } catch (err) {
                    console.error('Error searching attendees:', err);
                    setSearchResults([]);
                  } finally {
                    setSearchLoading(false);
                  }
                } else {
                  setSearchResults([]);
                }
              }}
              onChange={(event, value) => {
                if (value && typeof value === 'string') {
                  // Check if this value exists in searchResults (user selected from dropdown)
                  const selectedAttendee = searchResults.find(a => a.email === value);
                  if (selectedAttendee) {
                    // User selected from dropdown - auto-fill all fields
                    setFormData({
                      ...formData,
                      name: selectedAttendee.name,
                      phone: selectedAttendee.phone || '',
                      email: selectedAttendee.email || '',
                      tags: selectedAttendee.tags || formData.tags
                    });
                  } else {
                    // User typed a new value that's not in the list
                    setFormData({ ...formData, email: value });
                  }
                } else {
                  // Value cleared
                  setFormData({ ...formData, email: '' });
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Email"
                  type="email"
                  fullWidth
                />
              )}
            />

            <Autocomplete
              multiple
              freeSolo
              options={tagOptions}
              value={formData.tags}
              loading={tagOptionsLoading}
              onChange={(event, newValue) => handleTagSelectionChange(newValue)}
              filterSelectedOptions
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Labels / Tags"
                  placeholder="Add labels"
                  fullWidth
                />
              )}
            />

            {dialogMode === 'property' && (
              <>
                <FormControl fullWidth>
                  <InputLabel>Visit Type</InputLabel>
                  <Select
                    value={formData.visitType}
                    onChange={(e) => setFormData({ ...formData, visitType: e.target.value as any })}
                    label="Visit Type"
                  >
                    <MenuItem value="viewing">Viewing</MenuItem>
                    <MenuItem value="inspection">Inspection</MenuItem>
                    <MenuItem value="open_house">Open House</MenuItem>
                    <MenuItem value="private_viewing">Private Viewing</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label="Notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  fullWidth
                  multiline
                  rows={3}
                />

                <Divider sx={{ my: 1 }} />

                <Typography variant="subtitle2">Visitor Feedback for Report</Typography>

                <FormControl fullWidth>
                  <InputLabel>Interest Level</InputLabel>
                  <Select
                    value={formData.interestLevel}
                    onChange={(e) => setFormData({ ...formData, interestLevel: e.target.value as any })}
                    label="Interest Level"
                  >
                    <MenuItem value="">Not recorded</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="none">No interest</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label="Visitor Feedback"
                  value={formData.feedback}
                  onChange={(e) => setFormData({ ...formData, feedback: e.target.value })}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="What did the visitor say about the property?"
                />

                <TextField
                  label="Price Feedback"
                  value={formData.priceFeedback}
                  onChange={(e) => setFormData({ ...formData, priceFeedback: e.target.value })}
                  fullWidth
                />

                <TextField
                  label="Objections"
                  value={formData.objections}
                  onChange={(e) => setFormData({ ...formData, objections: e.target.value })}
                  fullWidth
                  placeholder="Concerns, blockers, or reasons for hesitation"
                />

                <FormControl fullWidth>
                  <InputLabel>Follow-up Intent</InputLabel>
                  <Select
                    value={formData.followUpIntent}
                    onChange={(e) => setFormData({ ...formData, followUpIntent: e.target.value as any })}
                    label="Follow-up Intent"
                  >
                    <MenuItem value="">Not recorded</MenuItem>
                    <MenuItem value="wants_contract">Wants contract</MenuItem>
                    <MenuItem value="wants_second_inspection">Wants second inspection</MenuItem>
                    <MenuItem value="considering">Considering</MenuItem>
                    <MenuItem value="not_interested">Not interested</MenuItem>
                    <MenuItem value="no_follow_up">No follow-up</MenuItem>
                  </Select>
                </FormControl>

                {property.saleType === 'rental' ? (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.attendeeRequestsApplicationForm}
                        onChange={(e) => setFormData({ ...formData, attendeeRequestsApplicationForm: e.target.checked })}
                      />
                    }
                    label="Visitor is interested – wants application form"
                  />
                ) : (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.attendeeRequestsContract}
                        onChange={(e) => setFormData({ ...formData, attendeeRequestsContract: e.target.checked })}
                      />
                    }
                    label="Attendee requests contract"
                  />
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setDialogOpen(false);
              setIsEditing(false);
              setEditingAttendeeId(null);
              setFormData({ sessionId: '', name: '', email: '', phone: '', notes: '', visitDate: '', visitType: 'open_house', feedback: '', interestLevel: '', priceFeedback: '', followUpIntent: '', objections: '', attendeeRequestsContract: false, attendeeRequestsApplicationForm: false, tags: [] });
              setError(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRegisterAttendee}
            disabled={submitting || !formData.name || !formData.phone}
            startIcon={isEditing ? <EditIcon /> : <PersonAddIcon />}
          >
            {submitting 
              ? (isEditing ? 'Saving...' : 'Recording...') 
              : (isEditing ? 'Save Changes' : 'Record Attendee')
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Attendee</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{attendeeToDelete?.name}</strong>?
          </Typography>
          <Typography color="warning.main" sx={{ mt: 2 }}>
            This action cannot be undone. All attendee information and history will be permanently removed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDeleteDialogOpen(false);
            setAttendeeToDelete(null);
          }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PropertyAttendees;

